import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { useScreenSize } from '@/hooks';
import styles from './Splash.module.css';

/**
 * Animated Splash Screen Component
 * Uses GSAP for smooth, optimized animations
 * Shows mobile logo on mobile, web logo on desktop
 * Works as an overlay component (not a route)
 */
const Splash = ({ onComplete }) => {
  const { isMobile } = useScreenSize();
  const [isLoading, setIsLoading] = useState(true);
  
  const splashRef = useRef(null);
  const logoRef = useRef(null);
  const loaderRef = useRef(null);
  const particlesRef = useRef([]);

  useEffect(() => {
    if (!splashRef.current || !logoRef.current || !loaderRef.current) return;

    const tl = gsap.timeline();

    // Set initial states
    gsap.set(logoRef.current, { 
      scale: 0.5, 
      opacity: 0,
      rotation: -10 
    });
    gsap.set(loaderRef.current, { opacity: 0, y: 20 });
    
    // Set particle initial states
    particlesRef.current.forEach((particle) => {
      if (particle) {
        gsap.set(particle, { 
          scale: 0, 
          opacity: 0,
          rotation: 0 
        });
      }
    });

    // Logo entrance animation
    tl.to(logoRef.current, {
      scale: 1,
      opacity: 1,
      rotation: 0,
      duration: 0.8,
      ease: 'back.out(1.7)',
    })
    // Logo pulse effect
    .to(logoRef.current, {
      scale: 1.05,
      duration: 0.3,
      yoyo: true,
      repeat: 1,
      ease: 'power2.inOut',
    })
    // Loader fade in
    .to(loaderRef.current, {
      opacity: 1,
      y: 0,
      duration: 0.5,
      ease: 'power2.out',
    }, '-=0.3')
    // Particle animation
    .to(particlesRef.current, {
      scale: 1,
      opacity: 0.6,
      rotation: 360,
      duration: 1.5,
      stagger: 0.1,
      ease: 'power2.out',
    }, '-=0.5');

    // Auto complete after animation
    const timer = setTimeout(() => {
      // Exit animation
      gsap.to(splashRef.current, {
        opacity: 0,
        scale: 0.95,
        duration: 0.5,
        ease: 'power2.in',
        onComplete: () => {
          setIsLoading(false);
          // Call onComplete callback to notify parent
          if (onComplete) {
            onComplete();
          }
        },
      });
    }, 2500);

    return () => {
      clearTimeout(timer);
      tl.kill();
    };
  }, [onComplete]);

  if (!isLoading) return null;

  const logoPath = isMobile 
    ? '/assets/images/logo/web-logo.webp'
    : '/assets/images/logo/web-logo.webp';

  return (
    <div ref={splashRef} className={styles.splash}>
      <div className={styles.container}>
        {/* Particles background */}
        <div className={styles.particlesContainer}>
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              ref={(el) => {
                if (el) particlesRef.current[i] = el;
              }}
              className={styles.particle}
              style={{
                '--angle': `${i * 60}deg`,
              }}
            />
          ))}
        </div>

        {/* Logo */}
        <div ref={logoRef} className={styles.logoContainer}>
          <img
            src={logoPath}
            alt="JACKOPOT"
            className={styles.logo}
            loading="eager"
            onError={(e) => {
              console.error('Logo failed to load:', logoPath);
              e.target.style.display = 'none';
            }}
          />
        </div>

        {/* Loader */}
        <div ref={loaderRef} className={styles.loader}>
          <div className={styles.loaderBar}>
            <div className={styles.loaderProgress}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Splash;
